// @flow
import React, { Fragment } from 'react';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';
import { Route } from 'react-router-dom';
import { H1 } from '@finect/tabular-core/Headings';
import { NotFound } from '@finect/tabular-components/NotFound';
import { LoadingBar } from '@finect/tabular-components/LoadingBar';
import { finectAPI } from '@finect/front-utils/finectAPI';
import { getUrlParameter } from '@finect/front-utils/getUrlParameter';
import { Datalayer } from '@finect/front-gtm';
import { SHARE_IMAGE } from '../../data/seo';
import {
  NewSectionInner,
  BottomNavigation,
  Filters,
  ListHeaderStacked,
  LocationFilters,
  InsideNavigation,
  MainGrey,
  Pagination,
  SectionTagTitle,
  CardList,
  CardItemList,
  BottomCTA
} from '../../components';
import type { CompanyType } from '../../types';
import { CompanyCard } from './partials/CompanyCard';

const PAGE_LENGTH = 20;
const BASE_PATH = '/asesores/encuentra-un-asesor/empresas';

const ListTitle = styled.div`
  position: relative;
  margin-top: -12px;

  &:before {
    position: absolute;
    top: 24px;
    left: 0;
    right: 0;
    border-top: 1px solid ${({ theme }) => theme.borderColor};
    content: '';
  }

  div > div {
    position: relative;
    z-index: 1;
    border: 12px solid ${({ theme }) => theme.colors.uiLL};
  }
`;

const initialValues = {
  selectedLocation: -1
};

type Props = {
  staticContext?: Object,
  history: Object,
  location: Object,
  match: Object,
  currentPage?: number,
  toggleModal: Function
};

type State = {
  companies: Array<CompanyType>,
  items: number,
  pages: number,
  currentPage: number,
  prevPage: number | null,
  selectedLocation: number,
  loading: boolean
};

export class Companies extends React.Component<Props, State> {
  locationSelect: HTMLSelectElement | null;
  locationSelectMobile: HTMLSelectElement | null;
  list: any;

  state =
    typeof window === 'undefined'
      ? {
        companies: this.props.staticContext ? this.props.staticContext.initialData.companies.data : [],
        items: this.props.staticContext ? this.props.staticContext.initialData.companies.paging.totalItems : 0,
        pages: this.props.staticContext
          ? Math.ceil(this.props.staticContext.initialData.companies.paging.totalItems / PAGE_LENGTH)
          : 0,
        currentPage: Number(getUrlParameter('pagina', this.props.location.search)) || 1,
        prevPage: Number(getUrlParameter('pagina', this.props.location.search)) || 1,
        ...initialValues,
        // Con datos del SSR no hay nada que esperar: si arrancara en true, el HTML
        // servido saldría con la barra de carga encendida sobre un listado completo,
        // porque en servidor no corre componentDidMount para apagarla.
        loading: !(this.props.staticContext && this.props.staticContext.initialData.companies.data.length)
      }
      : {
        companies: window.initialData ? window.initialData.companies.data : [],
        items: window.initialData ? window.initialData.companies.paging.totalItems : 0,
        pages: window.initialData ? Math.ceil(window.initialData.companies.paging.totalItems / PAGE_LENGTH) : 0,
        currentPage: Number(getUrlParameter('pagina', this.props.location.search)) || 1,
        prevPage: Number(getUrlParameter('pagina', this.props.location.search)) || 1,
        ...initialValues,
        loading: !(window.initialData && window.initialData.companies.data.length)
      };

  componentDidMount() {
    if (!this.state.companies.length) {
      const page = Number(getUrlParameter('pagina', this.props.location.search)) || 1;

      Companies.loadInitialData(null, null, this.props.location.search).then(
        companies => companies && companies.data && this.updatePagination(companies, page, true)
      );
      if (getUrlParameter('pagina', this.props.location.search) === '1') {
        this.props.history.push(BASE_PATH);
      }
    } else {
      this.setState({ loading: false });
    }
  }

  componentDidUpdate(prevProps: Object, prevState: Object) {
    const { currentPage, selectedLocation } = this.state;

    if (prevState.currentPage !== currentPage) {
      // Aquí la location va en el segundo de los parámetros, en un obj
      Companies.loadInitialData(null, {
        location: selectedLocation > 0 ? selectedLocation : 0,
        page: currentPage
      }).then(companies => this.updatePagination(companies, currentPage));
    }
  }

  // Lee la página de la URL y, si ha cambiado, la lleva al estado para que
  // componentDidUpdate dispare la petición. Hay que recordar la última página vista
  // (prevPage): sin ella la comparación siempre da distinto y `loading` se quedaba
  // fijado a true en cada render.
  static getDerivedStateFromProps(props: Object, state: Object) {
    const newPage = Number(getUrlParameter('pagina', props.location.search)) || 1;

    if (state.prevPage === newPage) return null;

    return {
      prevPage: newPage,
      currentPage: newPage,
      loading: true
    };
  }

  updatePagination = (companies: Object, page: number, preventScroll?: boolean) =>
    this.setState(
      {
        companies: companies.data || [],
        items: companies.paging.totalItems,
        pages: Math.ceil(companies.paging.totalItems / PAGE_LENGTH) || 0,
        currentPage: page,
        loading: false
      },
      () => {
        if (this.list && !preventScroll) {
          window.scrollTo({
            top: this.list.offsetTop,
            left: 0,
            behavior: 'smooth'
          });
        }
      }
    );

  handleChange = (currentSelected: number, selectedLocation: number) => {
    this.setState({
      selectedLocation: currentSelected,
      loading: true
    });

    if (getUrlParameter('pagina', this.props.location.search)) {
      this.setState({ selectedLocation });
      this.props.history.push(BASE_PATH);
    } else {
      const params = selectedLocation === -1 ? {} : { location: selectedLocation, page: 1 };

      // aquí se mandan dos parámetros...
      Companies.loadInitialData(null, params).then(
        companies =>
          companies &&
          companies.data &&
          this.setState({
            companies: companies.data || [],
            items: companies.paging.totalItems,
            pages: Math.ceil(companies.paging.totalItems / PAGE_LENGTH) || 0,
            currentPage: 1,
            selectedLocation,
            loading: false
          })
      );
    }
  };

  redirectTo = (url: string) => {
    this.props.history.push(url);
  };
  // $FlowFixMe
  static loadInitialData(cookie, params, url) {
    // En la url se me pasa la querystring para buscar el número de página
    // también se me puede pasar por params.page
    const page = url ? Number(getUrlParameter('pagina', url)) : (params && params.page) || 1;

    const queryParams = {
      location: (params && params.location) || 0,
      limit: PAGE_LENGTH,
      offset: page ? (page - 1) * PAGE_LENGTH : 0
    };
    const query = queryParams ? Object.keys(queryParams).map(key => `${key}=${queryParams[key]}`) : [];

    return finectAPI({
      version: 'v3',
      path: `advisers/companies${query.length ? `?${query.join('&')}` : ''}`,
      method: 'GET',
      credentials: cookie,
      onSuccess: (companies: Object) => companies,
      onError: () => console.log('Request to advisers/companies failed!'), // eslint-disable-line
    });
  }

  render() {
    const { companies, items, currentPage, pages, selectedLocation, loading } = this.state;
    const { staticContext, location, toggleModal } = this.props;
    const currentPath = location.pathname.replace(/\/\s*$/, '');
    const groupCompanies = companies.filter(item => item.urlFriendly);
    const externalCompanies = companies.filter(item => !item.urlFriendly);
    const baseURL = `https://${process.env.FINECT_BASEURL || 'www.finect.com'}`;

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Inicio',
          item: baseURL
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Asesores financieros',
          item: `${baseURL}/asesores`
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: 'Empresas',
          item: `${baseURL}/asesores/encuentra-un-asesor/empresas`
        }
      ]
    };

    // Check if 404 question not found
    if (!companies.length && !loading) {
      return (
        <Route
          render={props => (
            <NotFound
              message="Lo sentimos pero parece que esta página no existe."
              actionText="Volver al listado de empresas"
              redirectPath="/asesores/encuentra-un-asesor"
              {...props}
            />
          )}
        />
      );
    }

    return (
      <Fragment>
        <InsideNavigation
          links={[
            {
              title: 'Empresas',
              path: '/asesores/encuentra-un-asesor/empresas'
            }
          ]}
          currentPath={currentPath}
          buttonText="¿Necesitas ayuda?"
          redirectTo={this.redirectTo}
          toggleModal={toggleModal}
        />
        <MainGrey minHeight={!companies.length}>
          <Helmet>
            <title>Encuentra un asesor financiero - Empresas - Finect Asesores</title>
            <meta
              name="description"
              content="Encuentra una empresa de asesoramiento de confianza que te ayude a planificar tus finanzas para conseguir tus objetivos."
            />
            <link rel="canonical" href={`${baseURL}/asesores/encuentra-un-asesor/empresas`} />
            <meta name="robots" content="index, follow" />

            <meta property="og:site_name" content="Finect" />
            <meta property="og:type" content="website" />
            <meta property="og:title" content="Encuentra un asesor financiero - Empresas - Finect Asesores" />
            <meta
              property="og:description"
              content="Encuentra una empresa de asesoramiento de confianza que te ayude a planificar tus finanzas para conseguir tus objetivos."
            />
            <meta property="og:url" content={`${baseURL}/asesores/encuentra-un-asesor/empresas`} />
            <meta property="og:locale" content="es_ES" />
            <meta property="og:image" content={SHARE_IMAGE} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="675" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:site" content="@finect" />
            <meta property="twitter:title" content="Encuentra un asesor financiero - Empresas - Finect Asesores" />
            <meta
              property="twitter:description"
              content="Encuentra una empresa de asesoramiento de confianza que te ayude a planificar tus finanzas para conseguir tus objetivos."
            />
            <meta property="twitter:image" content={SHARE_IMAGE} />

            <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
          </Helmet>

          <Datalayer id="Scene" type="ready" params={{ pageType: 'advisersCompanies' }} />
          <Datalayer id="Scene" type="pageType" params={{ pageType: 'advisersCompanies' }} />

          <LoadingBar loading={loading} />
          <NewSectionInner>
            <ListHeaderStacked justify="space-between" align="center" wrap="wrap">
              {/* <h1>{this.props.match.params.page}</h1> */}
              <H1 large>Encuentra un asesor</H1>
              <Filters ref={el => (this.list = el)}>
                <LocationFilters
                  staticContext={staticContext}
                  selectedLocation={selectedLocation}
                  handleChange={this.handleChange}
                />
              </Filters>
            </ListHeaderStacked>
            {groupCompanies.length ? (
              <CardList>
                {groupCompanies.map((company, index) => (
                  // $FlowFixMe
                  <CompanyCard key={company.id} index={index} userLocation={selectedLocation} {...company} />
                ))}
              </CardList>
            ) : null}
            {externalCompanies.length ? (
              <ListTitle>
                <SectionTagTitle white>No están en Finect</SectionTagTitle>
              </ListTitle>
            ) : null}
            {externalCompanies.length ? (
              <CardItemList>
                {externalCompanies.map((company, index) => (
                  // $FlowFixMe
                  <CompanyCard key={company.id} index={groupCompanies.length + index} {...company} />
                ))}
              </CardItemList>
            ) : null}
            {pages > 1 ? (
              <Pagination
                basePath={BASE_PATH}
                currentPage={currentPage}
                pages={pages}
                items={items}
                pageLength={PAGE_LENGTH}
                name="empresas"
              />
            ) : null}
          </NewSectionInner>
        </MainGrey>
        <BottomCTA toggleModal={toggleModal} white />
        <BottomNavigation
          links={[
            { title: 'Asesores', path: '/asesores' },
            {
              title: 'Encuentra un asesor financiero',
              path: '/asesores/encuentra-un-asesor'
            },
            { title: 'Empresas' }
          ]}
        />
      </Fragment>
    );
  }
}
